"use client"

import { useRef, useState, useEffect } from "react"
import { Input } from "../ui/input"
import { Button } from "../ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { ScrollArea } from "../ui/scroll-area"
import { Badge } from "../ui/badge"
import { toast } from "sonner"
import { post, get } from "../../lib/api"
import JSZip from "jszip"
import { useSidebar } from "../ui/sidebar"
import * as XLSX from "xlsx"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts'
import { readFileWithEncodingDetection, readZipFileWithEncodingDetection } from "../../lib/encoding-utils"

// API function to get the next available date
const getNextAvailableDate = async (game_id) => {
    try {
        const response = await get(`/challenges?method=getLastDate&game_id=${game_id}`)

        if (response.error) {
            throw new Error(response.error)
        }

        // Calculate next available date from the last date
        const lastDate = response.lastDate
        if (lastDate) {
            const nextDate = new Date(lastDate)
            nextDate.setDate(nextDate.getDate() + 1)
            return { nextAvailableDate: nextDate.toISOString().split("T")[0] }
        } else {
            // If no last date, use today
            return { nextAvailableDate: new Date().toISOString().split("T")[0] }
        }
    } catch (error) {
        console.error("API call failed:", error)
        throw error
    }
}

// Helper function to extract filename without folder path
const getFileNameFromPath = (filePath) => {
    // Remove folder paths (handles both forward and backward slashes)
    const fileName = filePath.split(/[/\\]/).pop()
    return fileName
}

// Valid character whitelist
const VALID_CHARACTERS = "!\"#$%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[]^_`abcdefghijklmnopqrstuvwxyz{|}~ÁáÀàÄäÉéÈèÊêËëÍíÎîÏïÓóÔôÖöÚúÛûÜüÇç"

// Check if a character is valid
const isValidCharacter = (char) => {
    // Allow space, newline, and backslash characters
    if (char === ' ' || char === '\n' || char === '\r' || char === '\t' || char === '\\') {
        return true
    }
    return VALID_CHARACTERS.includes(char)
}

// Find invalid characters in text
const findInvalidCharacters = (text) => {
    const invalidChars = new Set()
    for (let i = 0; i < text.length; i++) {
        const char = text[i]
        if (!isValidCharacter(char)) {
            invalidChars.add(char)
        }
    }
    return Array.from(invalidChars)
}

// Check if text has invalid characters
const hasInvalidCharacters = (text) => {
    return findInvalidCharacters(text).length > 0
}

// Component to render text with invalid characters highlighted
const ValidatedTextArea = ({ value, onChange, placeholder, className, rows }) => {
    const editorRef = useRef(null)
    const isUpdatingRef = useRef(false)
    const [invalidChars, setInvalidChars] = useState([])
    
    useEffect(() => {
        const invalid = findInvalidCharacters(value || "")
        setInvalidChars(invalid)
    }, [value])
    
    const updateContent = (text) => {
        if (!editorRef.current) return
        
        const selection = window.getSelection()
        const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null
        const savedOffset = range ? range.startOffset : 0
        
        // Clear and rebuild content
        editorRef.current.innerHTML = ""
        
        if (!text) {
            const placeholderSpan = document.createElement('span')
            placeholderSpan.className = 'text-gray-400 opacity-50'
            placeholderSpan.textContent = placeholder
            editorRef.current.appendChild(placeholderSpan)
        } else {
            const lines = text.split('\n')
            lines.forEach((line, lineIndex) => {
                const lineDiv = document.createElement('div')
                line.split('').forEach((char) => {
                    const span = document.createElement('span')
                    const isValid = isValidCharacter(char)
                    if (!isValid) {
                        span.className = 'text-red-600 dark:text-red-400 font-bold bg-red-100 dark:bg-red-900'
                    }
                    span.textContent = char === ' ' ? '\u00A0' : char
                    lineDiv.appendChild(span)
                })
                editorRef.current.appendChild(lineDiv)
                if (lineIndex < lines.length - 1) {
                    editorRef.current.appendChild(document.createElement('br'))
                }
            })
        }
        
        // Restore cursor position
        if (range && editorRef.current.firstChild) {
            try {
                const newRange = document.createRange()
                const textNode = editorRef.current.firstChild
                const maxOffset = textNode.textContent ? textNode.textContent.length : 0
                newRange.setStart(textNode, Math.min(savedOffset, maxOffset))
                newRange.collapse(true)
                selection.removeAllRanges()
                selection.addRange(newRange)
            } catch (e) {
                // Ignore cursor restoration errors
            }
        }
    }
    
    // Update the contentEditable div when value changes externally
    useEffect(() => {
        if (editorRef.current && !isUpdatingRef.current) {
            const currentText = editorRef.current.textContent || ""
            if (currentText !== (value || "")) {
                isUpdatingRef.current = true
                updateContent(value || "")
                isUpdatingRef.current = false
            }
        }
    }, [value])
    
    const handleInput = (e) => {
        if (isUpdatingRef.current) return
        
        const newValue = editorRef.current.textContent || ""
        onChange({ target: { value: newValue } })
        
        // Re-render with highlighting after a short delay to allow cursor to settle
        setTimeout(() => {
            if (editorRef.current && !isUpdatingRef.current) {
                const selection = window.getSelection()
                const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null
                const savedOffset = range ? range.startOffset : 0
                const savedContainer = range ? range.startContainer : null
                
                isUpdatingRef.current = true
                updateContent(newValue)
                
                // Restore cursor position after update
                if (savedContainer && editorRef.current) {
                    try {
                        const newRange = document.createRange()
                        const textNodes = editorRef.current.querySelectorAll('span')
                        let currentOffset = 0
                        let targetNode = null
                        let targetOffset = 0
                        
                        for (const node of textNodes) {
                            const nodeLength = node.textContent.length
                            if (currentOffset + nodeLength >= savedOffset) {
                                targetNode = node.firstChild || node
                                targetOffset = savedOffset - currentOffset
                                break
                            }
                            currentOffset += nodeLength
                        }
                        
                        if (targetNode) {
                            newRange.setStart(targetNode, Math.min(targetOffset, targetNode.textContent.length))
                            newRange.collapse(true)
                            selection.removeAllRanges()
                            selection.addRange(newRange)
                        }
                    } catch (e) {
                        // Ignore cursor restoration errors
                    }
                }
                
                isUpdatingRef.current = false
            }
        }, 10)
    }
    
    const handlePaste = (e) => {
        e.preventDefault()
        const text = e.clipboardData.getData('text/plain')
        const selection = window.getSelection()
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0)
            range.deleteContents()
            const textNode = document.createTextNode(text)
            range.insertNode(textNode)
            range.setStartAfter(textNode)
            range.collapse(false)
            selection.removeAllRanges()
            selection.addRange(range)
        }
        handleInput(e)
    }
    
    return (
        <div className="space-y-1">
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onPaste={handlePaste}
                className={className}
                style={{
                    minHeight: rows ? `${rows * 1.5}rem` : 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    outline: 'none'
                }}
                data-placeholder={placeholder}
            />
            {invalidChars.length > 0 && (
                <div className="text-sm text-red-600 dark:text-red-400 mt-1">
                    ⚠️ Invalid characters found: {invalidChars.map(char => {
                        if (char === ' ') return '[space]'
                        if (char === '\n') return '[newline]'
                        if (char === '\t') return '[tab]'
                        return char
                    }).join(', ')}
                </div>
            )}
        </div>
    )
}

// Add at the top-level of the component
const InfoOverlay = ({ open, onClose }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 ">
            <div className="bg-[#ffffff] rounded-lg shadow-lg max-w-lg w-full p-6 relative dark:bg-[#2c313c] dark:text-white">
                <button
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl font-bold"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </button>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    {/* Provided SVG icon: bold 'i' in a circle, black */}
                    <svg width="22" height="22" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="256" cy="256" r="216" stroke="black" strokeWidth="32" fill="none"/>
                        <rect x="232" y="200" width="48" height="160" rx="24" fill="black"/>
                        <circle cx="256" cy="144" r="24" fill="black"/>
                    </svg>
                    How does the uploader work?
                </h2>
                <div className="space-y-3 text-gray-800 text-sm dark:text-white">
                    <p>
                        The uploader takes in either <b>one single .txt file</b> with challenge data or a <b>.zip file</b> containing multiple .txt files. The names of the .txt files will be used as the challenge names, but you can change these in the uploader after upload.
                    </p>
                    <p>
                        <b>Example of a challenge file:</b>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white">
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 1PETRUS</div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 1LOPER</div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 1BAARD</div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 1NOTENBALK </div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 2CANVAS </div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 2ZEIL</div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> ...</div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 4BRUG</div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 4BOK</div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 4BALK</div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white">  </div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 1Associatie met sleutel</div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 2Vloerbedekking</div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 3Zeil van een schip</div>
                        <div className="bg-gray-100 rounded p-0.5 text-xs overflow-x-auto dark:bg-gray-800 dark:text-white"> 4Turntoestel </div>

                        </div>
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>After uploading, select the date you wish the challenge to be live and click <b>Confirm</b>. This checks for date conflicts and validates the challenge format.</li>
                        <li>Once confirmed, click <b>Upload</b> for the selected date. Your challenge will be live for that date.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

// Download Modal Component
const DownloadModal = ({ open, onClose, onExport }) => {
    const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0])
    const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0])
    const [isExporting, setIsExporting] = useState(false)

    if (!open) return null

    const handleExport = async (format) => {
        if (!startDate || !endDate) {
            toast.error("Please select both start and end dates")
            return
        }

        if (new Date(startDate) > new Date(endDate)) {
            toast.error("Start date cannot be after end date")
            return
        }

        setIsExporting(true)
        try {
            await onExport(format, startDate, endDate)
            toast.success(`${format.toUpperCase()} export completed!`)
            onClose()
        } catch (error) {
            console.error("Export failed:", error)
            toast.error("Export failed. Please try again.")
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
            <div className="bg-[#ffffff] rounded-lg shadow-lg max-w-md w-full p-6 relative dark:bg-[#2c313c] dark:text-white">
                <button
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl font-bold"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </button>
                <h2 className="text-xl font-bold mb-4">Export Statistics Data</h2>
                
                <div className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Start Date</label>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="border-gray-300 focus:border-purple-500 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        />
                    </div>
                    
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">End Date</label>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="border-gray-300 focus:border-purple-500 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        />
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                        <Button
                            onClick={() => handleExport('pdf')}
                            disabled={isExporting}
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium"
                        >
                            {isExporting ? "Exporting..." : "📄 Export PDF"}
                        </Button>
                        <Button
                            onClick={() => handleExport('excel')}
                            disabled={isExporting}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium"
                        >
                            {isExporting ? "Exporting..." : "📊 Export Excel"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

const ConnectionsPlanning = ({ onUpdate }) => {
    const { game_id } = useSidebar(); // Get game_id from sidebar context
    const [uploadedFiles, setUploadedFiles] = useState([])
    const [currentFileIndex, setCurrentFileIndex] = useState(0)
    const [nextAvailableDate, setNextAvailableDate] = useState(null)
    const [isLoadingDate, setIsLoadingDate] = useState(false)
    const [uploadMode, setUploadMode] = useState("single") // "single" or "bulk"
    const fileInputRef = useRef(null)
    const [showInfo, setShowInfo] = useState(false);
    
    // Statistics state
    const [statsDate, setStatsDate] = useState(new Date().toISOString().split("T")[0])
    const [statsData, setStatsData] = useState(null)
    const [isLoadingStats, setIsLoadingStats] = useState(false)
    const [showDownloadModal, setShowDownloadModal] = useState(false)

    // Fetch next available date on component mount and after each upload
    useEffect(() => {
        if (game_id) {
            fetchNextAvailableDate()
        }
    }, [game_id])

    // Fetch statistics for today on component mount
    useEffect(() => {
        fetchStatsData(statsDate)
    }, [])

    // Fetch statistics when game_id changes
    useEffect(() => {
        if (game_id) {
            fetchStatsData(statsDate)
        }
    }, [game_id])

    const fetchNextAvailableDate = async () => {
        setIsLoadingDate(true)
        try {
            const response = await getNextAvailableDate(game_id)
            setNextAvailableDate(response.nextAvailableDate)

            // Update all unconfirmed files with new dates
            setUploadedFiles((prev) => {
                if (prev.length === 0) return prev

                const updatedFiles = prev.map((file, index) => {
                    if (!file.dateConfirmed && !file.uploaded) {
                        const newDate = getNextDate(response.nextAvailableDate, index)
                        return { ...file, selectedDate: newDate }
                    }
                    return file
                })
                return updatedFiles
            })
        } catch (error) {
            console.error("Failed to fetch next available date:", error)
            toast.error("Failed to fetch next available date")
            // Fallback to today's date
            setNextAvailableDate(new Date().toISOString().split("T")[0])
        } finally {
            setIsLoadingDate(false)
        }
    }

    const getNextDate = (dateString, daysToAdd = 0) => {
        const date = new Date(dateString)
        date.setDate(date.getDate() + daysToAdd)
        return date.toISOString().split("T")[0]
    }

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || [])
        if (files.length === 0) return

        const file = files[0] // We only handle one file at a time (either .txt or .zip)

        if (file.name.endsWith(".zip")) {
            await handleZipFile(file)
        } else if (file.type === "text/plain" || file.name.endsWith(".txt")) {
            await handleSingleTxtFile(file)
        } else {
            toast.error("Please upload a .txt file or a .zip file containing .txt files.")
            return
        }
    }

    const handleSingleTxtFile = async (file) => {
        setUploadMode("single")

        const fileContent = await readFileContent(file)
        const fileName = file.name
        const nameWithoutExtension = fileName.endsWith(".txt") ? fileName.slice(0, -4) : fileName

        // Use next available date as default
        const defaultDate = nextAvailableDate || new Date().toISOString().split("T")[0]

        const processedFile = {
            id: `${Date.now()}`,
            file,
            fileName,
            challengeName: nameWithoutExtension,
            fileContent,
            selectedDate: defaultDate,
            dateConfirmed: false,
            uploaded: false,
        }

        setUploadedFiles([processedFile])
        setCurrentFileIndex(0)
        toast.success("File loaded successfully!")
    }

    const handleZipFile = async (zipFile) => {
        setUploadMode("bulk")

        try {
            const zip = new JSZip()
            const zipContent = await zip.loadAsync(zipFile)

            const txtFiles = []

            // Extract all .txt files from the zip
            for (const [filename, file] of Object.entries(zipContent.files)) {
                if (!file.dir && filename.endsWith(".txt")) {
                    const content = await readZipFileWithEncodingDetection(file)
                    txtFiles.push({
                        filename,
                        content,
                    })
                }
            }

            if (txtFiles.length === 0) {
                toast.error("No .txt files found in the zip archive.")
                return
            }

            // Process each txt file
            const processedFiles = []
            const baseDate = nextAvailableDate || new Date().toISOString().split("T")[0]

            for (let i = 0; i < txtFiles.length; i++) {
                const txtFile = txtFiles[i]
                const cleanedContent = cleanFileContent(txtFile.content)

                // Extract just the filename without folder path
                const fileNameOnly = getFileNameFromPath(txtFile.filename)
                const nameWithoutExtension = fileNameOnly.endsWith(".txt") ? fileNameOnly.slice(0, -4) : fileNameOnly

                // Calculate date for this file (sequential dates starting from next available)
                const fileDate = getNextDate(baseDate, i)

                processedFiles.push({
                    id: `${Date.now()}-${i}`,
                    fileName: fileNameOnly, // Use filename without folder path
                    challengeName: nameWithoutExtension,
                    fileContent: cleanedContent,
                    selectedDate: fileDate,
                    dateConfirmed: false,
                    uploaded: false,
                })
            }

            setUploadedFiles(processedFiles)
            setCurrentFileIndex(0)
            toast.success(`${txtFiles.length} files extracted from zip and processed!`)
        } catch (error) {
            console.error("Error processing zip file:", error)
            toast.error("Failed to process zip file. Please ensure it's a valid zip archive.")
        }
    }

    const readFileContent = async (file) => {
        try {
            const text = await readFileWithEncodingDetection(file)
            const cleanedContent = cleanFileContent(text)
            return cleanedContent
        } catch (error) {
            console.error("File reading error:", error)
            throw error
        }
    }

    const cleanFileContent = (content) => {
        const lines = content.split("\n")
        const cleanedLines = []
        let blankLineInserted = false

        for (let i = 0; i < lines.length; i++) {
            const trimmed = lines[i].trim()
            if (!trimmed) {
                if (!blankLineInserted) {
                    cleanedLines.push("")
                    blankLineInserted = true
                }
                continue
            }
            const [group, ...rest] = trimmed.split(/\s+/)
            cleanedLines.push(group + rest.join(" "))
        }

        return cleanedLines.join("\n")
    }

    const updateFile = (index, updates) => {
        setUploadedFiles((prev) => prev.map((file, i) => (i === index ? { ...file, ...updates } : file)))
    }

    const handleDateConfirm = (index) => {
        const file = uploadedFiles[index]
        if (!file.selectedDate) {
            toast.error("Please select a date.")
            return
        }
        updateFile(index, { dateConfirmed: true })
        toast.success(`Date confirmed for ${file.fileName}`)
    }

    const handleDateChange = (index, newDate) => {
        updateFile(index, { selectedDate: newDate, dateConfirmed: false })
    }

    const uploadSingleFile = async (index) => {
        const file = uploadedFiles[index]

        if (!file.fileContent || file.challengeName.trim() === "") {
            toast.error("Please provide a challenge name and valid file content.")
            return
        }

        if (!file.dateConfirmed) {
            toast.error("Please confirm the upload date first.")
            return
        }

        try {
            // First POST request - Add the challenge
            const response = await post(
                {
                    method: "addChallengeNoDup",
                    game_id,
                    challenge_id: file.challengeName,
                    data: file.fileContent,
                    variant_id: 1,
                    variant_name: "challenge",
                },
                "/challenges",
            )

            if (response.error) {
                if (response.error === "Failed to fetch") {
                    toast.error(`Challenge "${file.challengeName}" already exists.`)
                } else {
                    toast.error(response.error)
                }
                return
            }

            // Second POST request - Schedule the challenge for the selected date
            const dailyResponse = await post(
                {
                    method: "addDailyChallenge",
                    game_id,
                    date: file.selectedDate,
                    daily_challenge_info: file.challengeName,
                },
                "/challenges",
            )

            if (dailyResponse.error) {
                toast.error(`Challenge uploaded but scheduling failed: ${dailyResponse.error}`)
                return
            }

            updateFile(index, { uploaded: true })
            toast.success(`"${file.fileName}" uploaded and scheduled for ${formatDateDisplay(file.selectedDate)}!`)
            
            // Trigger calendar refresh
            if (typeof onUpdate === 'function') onUpdate()

            // Refresh the next available date after successful upload
            await fetchNextAvailableDate()
        } catch (err) {
            toast.error(`Upload failed for "${file.fileName}"!`)
            console.error(err)
        }
    }

    const uploadAllConfirmed = async () => {
        const confirmedFiles = uploadedFiles.filter((file) => file.dateConfirmed && !file.uploaded)

        if (confirmedFiles.length === 0) {
            toast.error("No confirmed files to upload.")
            return
        }

        // Check for invalid characters in confirmed files
        const filesWithInvalidChars = confirmedFiles.filter((file) => hasInvalidCharacters(file.fileContent || ""))
        if (filesWithInvalidChars.length > 0) {
            toast.error(`Cannot upload: ${filesWithInvalidChars.length} file(s) contain invalid characters. Please fix them first.`)
            return
        }

        toast.info(`Uploading ${confirmedFiles.length} files...`)

        for (const file of confirmedFiles) {
            const index = uploadedFiles.findIndex((f) => f.id === file.id)
            await uploadSingleFile(index)
        }
    }

    const confirmAllDates = () => {
        setUploadedFiles((prev) => prev.map((file) => ({ ...file, dateConfirmed: true })))
        toast.success("All dates confirmed!")
    }

    const formatDateDisplay = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        })
    }

    const clearAll = () => {
        setUploadedFiles([])
        setCurrentFileIndex(0)
        setUploadMode("single")
        if (fileInputRef.current) {
            fileInputRef.current.value = ""
        }
    }

    // Fetch statistics data for selected date
    const fetchStatsData = async (date) => {
        setIsLoadingStats(true)
        try {
            const response = await fetch(`https://5p5viwhcwj.execute-api.eu-central-1.amazonaws.com/dapi3/rodedraad?date=${date}&game_id=${game_id}`)
            const data = await response.json()
            
            if (data.s === false && (data.distribution1 || data.distribution2)) {
                setStatsData(data)
            } else {
                setStatsData(null)
                toast.error("No data available for the selected date")
            }
        } catch (error) {
            console.error("Failed to fetch statistics:", error)
            toast.error("Failed to fetch statistics data")
            setStatsData(null)
        } finally {
            setIsLoadingStats(false)
        }
    }

    // Handle stats date change
    const handleStatsDateChange = (newDate) => {
        setStatsDate(newDate)
        fetchStatsData(newDate)
    }

    // Fetch data for date range
    const fetchDateRangeData = async (startDate, endDate) => {
        const data = []
        const currentDate = new Date(startDate)
        const end = new Date(endDate)
        
        while (currentDate <= end) {
            const dateStr = currentDate.toISOString().split("T")[0]
            try {
                const response = await fetch(`https://5p5viwhcwj.execute-api.eu-central-1.amazonaws.com/dapi3/rodedraad?date=${dateStr}&game_id=${game_id}`)
                const result = await response.json()
                
                if (result.s === false && (result.distribution1 || result.distribution2)) {
                    data.push({
                        date: dateStr,
                        distribution1: result.distribution1 || [],
                        distribution2: result.distribution2 || []
                    })
                }
            } catch (error) {
                console.error(`Failed to fetch data for ${dateStr}:`, error)
            }
            
            currentDate.setDate(currentDate.getDate() + 1)
        }
        
        return data
    }

    // Export to PDF (using browser print functionality)
    const exportToPDF = (data, startDate, endDate) => {
        // Create HTML content for PDF
        let htmlContent = `
            <html>
                <head>
                    <title>Connections Challenge Statistics</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        h1 { color: #333; }
                        h2 { color: #666; margin-top: 30px; }
                        h3 { color: #888; margin-top: 20px; }
                        table { border-collapse: collapse; width: 100%; margin: 10px 0; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #f2f2f2; }
                        .date-section { margin-bottom: 30px; }
                        .chart-note { background-color: #e3f2fd; padding: 10px; border-left: 4px solid #2196f3; margin: 15px 0; }
                        .difficulty-1 { background-color: rgb(119, 230, 149); }
                        .difficulty-2 { background-color: rgb(210, 201, 254); }
                        .difficulty-3 { background-color: rgb(255, 173, 147); }
                        .difficulty-4 { background-color: rgb(247, 190, 72); }
                    </style>
                </head>
                <body>
                    <h1>Connections Challenge Statistics</h1>
                    <p><strong>Date Range:</strong> ${startDate} to ${endDate}</p>
                    
                    <div class="chart-note">
                        <strong>📊 Interactive Charts Available:</strong> This PDF contains tabular data. 
                        For interactive charts and visualizations, please view the web interface at the same date range.
                    </div>
        `
        
        data.forEach((dayData) => {
            htmlContent += `
                <div class="date-section">
                    <h2>Date: ${dayData.date}</h2>
                    
                    ${dayData.distribution1 && dayData.distribution1.length > 0 ? `
                        <h3>Position Distribution (Chart Data)</h3>
                        <table>
                            <thead>
                                <tr>
                                    <th>Solved Order</th>
                                    <th>Difficulty 1 (Easy)</th>
                                    <th>Difficulty 2 (Medium)</th>
                                    <th>Difficulty 3 (Hard)</th>
                                    <th>Difficulty 4 (Hardest)</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${(() => {
                                    // Group by position for better chart representation
                                    const grouped = {}
                                    dayData.distribution1.forEach(item => {
                                        if (!grouped[item.position]) {
                                            grouped[item.position] = { position: item.position }
                                        }
                                        grouped[item.position][`difficulty${item.number}`] = Math.round(parseFloat(item.percentage))
                                    })
                                    
                                    const positionLabels = {
                                        '1': 'Solved First',
                                        '2': 'Solved Second', 
                                        '3': 'Solved Third',
                                        '4': 'Solved Fourth'
                                    }
                                    
                                    return Object.values(grouped).map(item => `
                                        <tr>
                                            <td><strong>${positionLabels[item.position] || `Position ${item.position}`}</strong></td>
                                            <td class="difficulty-1">${item.difficulty1 || 0}%</td>
                                            <td class="difficulty-2">${item.difficulty2 || 0}%</td>
                                            <td class="difficulty-3">${item.difficulty3 || 0}%</td>
                                            <td class="difficulty-4">${item.difficulty4 || 0}%</td>
                                        </tr>
                                    `).join('')
                                })()}
                            </tbody>
                        </table>
                        
                        <h3>Detailed Position Data</h3>
                        <table>
                            <thead>
                                <tr>
                                    <th>Position</th>
                                    <th>Difficulty</th>
                                    <th>Count</th>
                                    <th>Percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${dayData.distribution1.map(item => `
                                    <tr>
                                        <td>${item.position}</td>
                                        <td>${item.number}</td>
                                        <td>${item.count}</td>
                                        <td>${Math.round(parseFloat(item.percentage))}%</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    ` : ''}
                    
                    ${dayData.distribution2 && dayData.distribution2.length > 0 ? `
                        <h3>Mistakes Distribution</h3>
                        <table>
                            <thead>
                                <tr>
                                    <th>Mistakes</th>
                                    <th>Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${dayData.distribution2.map(item => `
                                    <tr>
                                        <td>${item.mistakes}</td>
                                        <td>${item.count}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    ` : ''}
                </div>
            `
        })
        
        htmlContent += `
                </body>
            </html>
        `
        
        // Create a new window and print
        const printWindow = window.open('', '_blank')
        printWindow.document.write(htmlContent)
        printWindow.document.close()
        printWindow.focus()
        
        // Wait for content to load, then print
        setTimeout(() => {
            printWindow.print()
            printWindow.close()
        }, 250)
    }

    // Export to Excel
    const exportToExcel = (data, startDate, endDate) => {
        const workbook = XLSX.utils.book_new()
        
        // Create a single sheet with all data
        const worksheetData = [
            ['Connections Challenge Statistics'],
            [`Date Range: ${startDate} to ${endDate}`],
            [''],
            ['Date', 'Position', 'Difficulty', 'Count', 'Percentage', 'Mistakes', 'Mistakes Count']
        ]
        
        data.forEach((dayData) => {
            // Add distribution1 data
            if (dayData.distribution1 && dayData.distribution1.length > 0) {
                dayData.distribution1.forEach((item, index) => {
                    worksheetData.push([
                        index === 0 ? dayData.date : '', // Only show date on first row for each day
                        item.position,
                        item.number,
                        item.count,
                        item.percentage,
                        '', // Empty for mistakes columns
                        ''  // Empty for mistakes count column
                    ])
                })
            }
            
            // Add distribution2 data
            if (dayData.distribution2 && dayData.distribution2.length > 0) {
                dayData.distribution2.forEach((item, index) => {
                    worksheetData.push([
                        index === 0 ? dayData.date : '', // Only show date on first row for each day
                        '', // Empty for position columns
                        '', // Empty for number column
                        '', // Empty for count column
                        '', // Empty for percentage column
                        item.mistakes,
                        item.count
                    ])
                })
            }
        })
        
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
        
        // Add some styling/formatting
        const range = XLSX.utils.decode_range(worksheet['!ref'])
        
        // Make header row bold
        for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 3, c: col }) // Row 4 (0-indexed)
            if (!worksheet[cellAddress]) continue
            worksheet[cellAddress].s = { font: { bold: true } }
        }
        
        // Set column widths
        worksheet['!cols'] = [
            { wch: 12 }, // Date column
            { wch: 10 }, // Position column
            { wch: 10 }, // Difficulty column
            { wch: 8 },  // Count column
            { wch: 12 }, // Percentage column
            { wch: 10 }, // Mistakes column
            { wch: 12 }  // Mistakes Count column
        ]
        
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Statistics')
        XLSX.writeFile(workbook, `connections-statistics-${startDate}-to-${endDate}.xlsx`)
    }

    // Transform distribution1 data for stacked bar chart
    const transformDistribution1Data = (distribution1) => {
        if (!distribution1 || distribution1.length === 0) return []
        
        try {
            console.log('Original distribution1 data:', distribution1)
            
            // Group by position
            const groupedByPosition = {}
            distribution1.forEach(item => {
                if (!groupedByPosition[item.position]) {
                    groupedByPosition[item.position] = {}
                }
                groupedByPosition[item.position][item.number] = parseFloat(item.percentage)
            })
            
            console.log('Grouped data:', groupedByPosition)
            
            // Convert to array format for stacked bars
            return Object.keys(groupedByPosition).map(position => {
                const positionLabels = {
                    '1': 'Solved First',
                    '2': 'Solved Second', 
                    '3': 'Solved Third',
                    '4': 'Solved Fourth'
                }
                const data = { position: positionLabels[position] || `Position ${position}` }
                const positionData = groupedByPosition[position]
                
                // Add each difficulty as a separate property
                const difficulties = ['1', '2', '3', '4']
                difficulties.forEach(difficulty => {
                    data[`difficulty${difficulty}`] = positionData[difficulty] || 0
                })
                
                return data
            })
        } catch (error) {
            console.error('Error transforming distribution1 data:', error)
            console.error('Distribution1 data:', distribution1)
            return []
        }
    }

    // Handle export from modal
    const handleExport = async (format, startDate, endDate) => {
        const data = await fetchDateRangeData(startDate, endDate)
        
        if (format === 'pdf') {
            exportToPDF(data, startDate, endDate)
        } else if (format === 'excel') {
            exportToExcel(data, startDate, endDate)
        }
    }

    const currentFile = uploadedFiles[currentFileIndex]
    const confirmedCount = uploadedFiles.filter((f) => f.dateConfirmed).length
    const uploadedCount = uploadedFiles.filter((f) => f.uploaded).length

    return (
        <div className="">
            {/* Info Overlay */}
            <InfoOverlay open={showInfo} onClose={() => setShowInfo(false)} />
            {/* Download Modal */}
            <DownloadModal open={showDownloadModal} onClose={() => setShowDownloadModal(false)} onExport={handleExport} />
            {/* Header with stats */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white m-0 p-0">Challenge Uploader</h3>
                    {isLoadingDate && <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">Loading next date...</span>}
                    {nextAvailableDate && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-0 dark:bg-blue-900 dark:text-blue-300 font-medium">
                            Next available: {formatDateDisplay(nextAvailableDate)}
                        </Badge>
                    )}
                </div>
                {uploadedFiles.length > 0 && (
                    <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="bg-gray-100 border-0 text-gray-800 dark:bg-gray-800  dark:text-gray-200 font-medium">
                            {uploadedFiles.length} file{uploadedFiles.length !== 1 ? "s" : ""}
                        </Badge>
                        <Badge variant="default" className="bg-blue-500 border-0 text-white dark:bg-blue-700 font-medium">
                            {confirmedCount} confirmed
                        </Badge>
                        <Badge className="bg-green-600 border-0 text-white dark:bg-green-700 font-medium">{uploadedCount} uploaded</Badge>
                    </div>
                )}
            </div>

            {/* File Upload */}
            <div className="space-y-3 mt-4">
                <Input
                    type="file"
                    accept=".txt,.zip"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                />
                <div className="flex gap-3">
                    <Button
                        className="bg-purple-700 hover:bg-purple-800 text-white font-medium px-6 py-2 shadow-sm"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload .txt File or .zip Archive
                    </Button>
                    {uploadedFiles.length > 0 && (
                        <>
                            {uploadMode === "bulk" && (
                                <Button
                                    onClick={confirmAllDates}
                                    variant="outline"
                                    className="border-1 border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-300 dark:hover:bg-blue-900 font-medium bg-transparent"
                                >
                                    Confirm All Dates
                                </Button>
                            )}
                            <Button 
                                onClick={uploadAllConfirmed} 
                                disabled={uploadedFiles.some((file) => file.dateConfirmed && !file.uploaded && hasInvalidCharacters(file.fileContent || ""))}
                                className={uploadedFiles.some((file) => file.dateConfirmed && !file.uploaded && hasInvalidCharacters(file.fileContent || ""))
                                    ? "bg-gray-400 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed font-medium"
                                    : "bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white font-medium"}
                            >
                                {uploadedFiles.some((file) => file.dateConfirmed && !file.uploaded && hasInvalidCharacters(file.fileContent || ""))
                                    ? "⛔ Fix Invalid Characters to Upload"
                                    : `Upload ${uploadMode === "single" ? "File" : `All Confirmed (${confirmedCount})`}`}
                            </Button>
                            <Button
                                onClick={clearAll}
                                variant="outline"
                                className="border-1 border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900 font-medium bg-transparent"
                            >
                                Clear All
                            </Button>
                        </>
                    )}
                </div>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-md mb-4 mt-4">
                    <span>
                        📁 Upload a single .txt file or a .zip archive containing multiple .txt files
                    </span>
                    {/* Info Button */}
                    <button
                        className="ml-2 p-1 rounded-full flex items-center justify-center"
                        onClick={() => setShowInfo(true)}
                        aria-label="How does the uploader work?"
                        type="button"
                    >
                        {/* Provided SVG icon: bold 'i' in a circle, black */}
                        <svg width="20" height="20" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="256" cy="256" r="216" stroke="currentColor" strokeWidth="40" fill="none"/>
                            <rect x="232" y="200" width="48" height="160" rx="24" fill="currentColor"/>
                            <circle cx="256" cy="144" r="24" fill="currentColor"/>
                        </svg>
                    </button>
                </div>
                
            </div>

            {uploadedFiles.length > 0 && (
                <>
                    {uploadMode === "single" ? (
                        // Single File Mode
                        <Card className="border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                            <CardHeader className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                                <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">Single File Upload</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-5 p-6">
                                {/* Challenge Name Input */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Challenge Name</label>
                                    <Input
                                        value={currentFile.challengeName}
                                        onChange={(e) => updateFile(0, { challengeName: e.target.value })}
                                        placeholder="Enter challenge name"
                                        className="border-gray-300 focus:border-purple-500 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                    />
                                </div>

                                {/* Date Selection */}
                                <div className="space-y-3">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Upload Date</label>
                                    <div className="flex items-center space-x-3">
                                        <Input
                                            type="date"
                                            value={currentFile.selectedDate}
                                            onChange={(e) => handleDateChange(0, e.target.value)}
                                            className="border-1 flex-1 border-gray-300 focus:border-purple-500 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                        />
                                        {!currentFile.dateConfirmed ? (
                                            <Button
                                                onClick={() => handleDateConfirm(0)}
                                                variant="outline"
                                                className="border-1 whitespace-nowrap border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-300 dark:hover:bg-blue-900 font-medium"
                                            >
                                                Confirm Date
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={() => updateFile(0, { dateConfirmed: false })}
                                                variant="outline"
                                                className="border-1 whitespace-nowrap bg-green-50 border-green-500 text-green-700 hover:bg-green-100 dark:bg-green-900 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-800 font-medium"
                                            >
                                                ✓ Confirmed
                                            </Button>
                                        )}
                                    </div>
                                    {currentFile.dateConfirmed && (
                                        <p className="text-sm text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900 p-2 rounded-md font-medium">
                                            📅 Scheduled for: {formatDateDisplay(currentFile.selectedDate)}
                                        </p>
                                    )}
                                </div>

                                {/* File Preview */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">File Preview</label>
                                    <ValidatedTextArea
                                        className="w-full p-3 border-2 border-gray-300 rounded-md bg-gray-50 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white max-h-60 overflow-auto whitespace-pre-wrap text-sm font-mono focus:border-purple-500 focus:ring-purple-500"
                                        rows={10}
                                        value={currentFile.fileContent}
                                        onChange={(e) => updateFile(0, { fileContent: e.target.value })}
                                        placeholder="Preview will appear here..."
                                    />
                                </div>

                                {/* Upload Button */}
                                <Button
                                    className={`w-full font-medium py-3 ${
                                        currentFile.uploaded
                                            ? "border-1 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white"
                                            : currentFile.dateConfirmed && !hasInvalidCharacters(currentFile.fileContent || "")
                                                ? "border-1 bg-purple-700 hover:bg-purple-800 dark:bg-purple-800 dark:hover:bg-purple-900 text-white"
                                                : "border-1 bg-gray-400 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                                    }`}
                                    onClick={() => uploadSingleFile(0)}
                                    disabled={!currentFile.dateConfirmed || currentFile.uploaded || hasInvalidCharacters(currentFile.fileContent || "")}
                                >
                                    {currentFile.uploaded
                                        ? "✅ Successfully Uploaded"
                                        : hasInvalidCharacters(currentFile.fileContent || "")
                                            ? "⛔ Fix Invalid Characters to Upload"
                                            : currentFile.dateConfirmed
                                                ? `🚀 Upload for ${new Date(currentFile.selectedDate).toLocaleDateString()}`
                                                : "⏳ Confirm Date to Enable Upload"}
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        // Bulk Mode (from zip)
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* File List */}
                            <Card className="border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                                <CardHeader className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                                    <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Files from Zip ({uploadedFiles.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-4">
                                    <ScrollArea className="h-80">
                                        <div className="space-y-3">
                                            {uploadedFiles.map((file, index) => (
                                                <div
                                                    key={file.id}
                                                    className={`p-4 rounded-lg cursor-pointer border-2 transition-all ${
                                                        index === currentFileIndex
                                                            ? "border-1 border-purple-400 bg-purple-50 dark:border-purple-500 dark:bg-purple-900 shadow-md"
                                                            : "border-1 border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                                                    }`}
                                                    onClick={() => setCurrentFileIndex(index)}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.fileName}</span>
                                                        <div className="flex gap-2">
                                                            {file.uploaded && (
                                                                <Badge className="border-1 text-xs bg-green-600 text-white font-medium">✅ Uploaded</Badge>
                                                            )}
                                                            {file.dateConfirmed && !file.uploaded && (
                                                                <Badge className="border-1 text-xs bg-blue-600 text-white font-medium">✓ Confirmed</Badge>
                                                            )}
                                                            {!file.dateConfirmed && (
                                                                <Badge
                                                                    variant="secondary"
                                                                    className="border-1 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 font-medium"
                                                                >
                                                                    ⏳ Pending
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 font-medium">
                                                        📅 {formatDateDisplay(file.selectedDate)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                </CardContent>
                            </Card>

                            {/* Current File Editor */}
                            {currentFile && (
                                <Card className="border-2 border-purple-200 dark:border-purple-700 shadow-sm">
                                    <CardHeader className="bg-purple-50 dark:bg-purple-900 border-b border-purple-200 dark:border-purple-700">
                                        <CardTitle className="text-lg font-semibold text-purple-900 dark:text-purple-100">
                                            Editing: {currentFile.fileName} ({currentFileIndex + 1}/{uploadedFiles.length})
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4 p-6">
                                        {/* Challenge Name Input */}
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Challenge Name</label>
                                            <Input
                                                value={currentFile.challengeName}
                                                onChange={(e) => updateFile(currentFileIndex, { challengeName: e.target.value })}
                                                placeholder="Enter challenge name"
                                                className="border-1 border-gray-300 focus:border-purple-500 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                            />
                                        </div>

                                        {/* Date Selection */}
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Upload Date</label>
                                            <div className="flex items-center space-x-2">
                                                <Input
                                                    type="date"
                                                    value={currentFile.selectedDate}
                                                    onChange={(e) => handleDateChange(currentFileIndex, e.target.value)}
                                                    className="border-1 flex-1 border-gray-300 focus:border-purple-500 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                                />
                                                {!currentFile.dateConfirmed ? (
                                                    <Button
                                                        onClick={() => handleDateConfirm(currentFileIndex)}
                                                        variant="outline"
                                                        className="border-1 whitespace-nowrap border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-300 dark:hover:bg-blue-900 font-medium"
                                                        size="sm"
                                                    >
                                                        Confirm
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        onClick={() => updateFile(currentFileIndex, { dateConfirmed: false })}
                                                        variant="outline"
                                                        className="border-1 whitespace-nowrap bg-green-50 border-green-500 text-green-700 hover:bg-green-100 dark:bg-green-900 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-800 font-medium"
                                                        size="sm"
                                                    >
                                                        ✓ Confirmed
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {/* File Preview */}
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">File Preview</label>
                                            <ValidatedTextArea
                                                className="w-full p-3 border-2 border-gray-300 rounded-md bg-gray-50 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white h-32 overflow-auto whitespace-pre-wrap text-xs font-mono focus:border-purple-500 focus:ring-purple-500"
                                                rows={8}
                                                value={currentFile.fileContent}
                                                onChange={(e) => updateFile(currentFileIndex, { fileContent: e.target.value })}
                                                placeholder="Preview will appear here..."
                                            />
                                        </div>

                                        {/* Individual Upload Button */}
                                        <Button
                                            className={`w-full font-medium py-2 ${
                                                currentFile.uploaded
                                                    ? "bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white"
                                                    : currentFile.dateConfirmed && !hasInvalidCharacters(currentFile.fileContent || "")
                                                        ? "bg-purple-700 hover:bg-purple-800 dark:bg-purple-800 dark:hover:bg-purple-900 text-white"
                                                        : "bg-gray-400 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                                            }`}
                                            onClick={() => uploadSingleFile(currentFileIndex)}
                                            disabled={!currentFile.dateConfirmed || currentFile.uploaded || hasInvalidCharacters(currentFile.fileContent || "")}
                                            size="sm"
                                        >
                                            {currentFile.uploaded
                                                ? "✅ Uploaded"
                                                : hasInvalidCharacters(currentFile.fileContent || "")
                                                    ? "⛔ Fix Invalid Characters to Upload"
                                                    : currentFile.dateConfirmed
                                                        ? `🚀 Upload for ${new Date(currentFile.selectedDate).toLocaleDateString()}`
                                                        : "⏳ Confirm Date to Enable Upload"}
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Statistics Section */}
            <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                <Card className="border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                    <CardHeader className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">Challenge Statistics</CardTitle>
                            <Button
                                onClick={() => setShowDownloadModal(true)}
                                variant="outline"
                                className="border-1 border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-600 dark:text-purple-300 dark:hover:bg-purple-900 font-medium bg-transparent"
                            >
                                📊 Download Data
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4 p-6">
                        {/* Date Selection */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Date for Statistics</label>
                            <div className="flex items-center space-x-3">
                                <Input
                                    type="date"
                                    value={statsDate}
                                    onChange={(e) => handleStatsDateChange(e.target.value)}
                                    className="border-1 border-gray-300 focus:border-purple-500 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                />
                                {isLoadingStats && (
                                    <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">Loading...</span>
                                )}
                            </div>
                        </div>

                        {/* Statistics Display */}
                        {statsData && (statsData.distribution1 || statsData.distribution2) && (
                            <div className="space-y-6">
                                {/* Distribution 1 Chart - Position/Number */}
                                {statsData.distribution1 && statsData.distribution1.length > 0 && (
                                    <div className="space-y-3">
                                        <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Position Distribution</h4>
                                        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-600" style={{backgroundColor: '#F7F2F2'}}>
                                            {(() => {
                                                const chartData = transformDistribution1Data(statsData.distribution1)
                                                console.log('Chart data:', chartData)
                                                
                                                if (chartData.length === 0) {
                                                    return <div className="text-center py-8 text-gray-500">No data available for chart</div>
                                                }
                                                
                                                return (
                                                    <ResponsiveContainer width="100%" height={300}>
                                                        <BarChart data={chartData}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis 
                                                                dataKey="position" 
                                                                label={{ value: '', position: 'insideBottom', offset: -5 }}
                                                            />
                                                            <YAxis 
                                                                label={{ value: 'Percentage', angle: -90, position: 'insideLeft' }}
                                                                tickFormatter={(value) => `${value}%`}
                                                                domain={[0, 100]}
                                                            />
                                                            <Tooltip 
                                                                formatter={(value, name) => [`${Math.round(value)}%`, `${name.replace('difficulty', '')}`]}
                                                                labelFormatter={(label) => label}
                                                            />
                                                            <Legend />
                                                            <Bar dataKey="difficulty1" stackId="a" fill="rgb(119, 230, 149)" name="Difficulty 1" />
                                                            <Bar dataKey="difficulty2" stackId="a" fill="rgb(210, 201, 254)" name="Difficulty 2" />
                                                            <Bar dataKey="difficulty3" stackId="a" fill="rgb(255, 173, 147)" name="Difficulty 3" />
                                                            <Bar dataKey="difficulty4" stackId="a" fill="rgb(247, 190, 72)" name="Difficulty 4" />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                )
                                            })()}
                                        </div>
                                    </div>
                                )}

                                {/* Distribution 2 Chart - Mistakes */}
                                {statsData.distribution2 && statsData.distribution2.length > 0 && (
                                    <div className="space-y-3">
                                        <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Mistakes Distribution</h4>
                                        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-600" style={{backgroundColor: '#F7F2F2'}}>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <BarChart data={statsData.distribution2}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis 
                                                        dataKey="mistakes" 
                                                        label={{ value: 'Number of Mistakes', position: 'insideBottom', offset: -5 }}
                                                    />
                                                    <YAxis 
                                                        label={{ value: 'Count', angle: -90, position: 'insideLeft' }}
                                                    />
                                                    <Tooltip 
                                                        formatter={(value) => [value, 'Count']}
                                                        labelFormatter={(label) => `${label} Mistakes`}
                                                    />
                                                    <Bar dataKey="count" fill="#FFAD93" name="Count" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {!statsData && !isLoadingStats && (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                Select a date to view statistics
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}

export default ConnectionsPlanning 